import {
  Box,
  Chip,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  createStyles,
} from '@mantine/core';
import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { isDark, useReplaceQueryParams } from '@/utils/utils';
import { useEffect, useState } from 'react';

import { API_ENDPOINT } from '@/config/endpoint';
import DMText from '@/components/DMText';
import QuestionMarkTooltip from '@/components/QuestionMarkTooltip';
import { mixpanelIdentifyEmail } from '@/utils/mixpanel';
import themeVariables from '@/styles/themeVariables';
import { useAtomValue } from 'jotai';
import { useDisclosure } from '@mantine/hooks';
import { userInfo } from '@/config/globalStateControl';

const createUserRegisterModalStyle = createStyles((theme, dark: any) => ({
  title: {
    fontWeight: 600,
  },

  description: {
    fontSize: 13,
    color: themeVariables.fontColors.extraLight,
    margin: '3px 0px 15px',
  },

  formLabel: {
    marginBottom: 8,
    color: dark?.dark
      ? themeVariables.fontColors.white
      : themeVariables.fontColors.medium,
    fontSize: 14,
    fontWeight: 600,
  },

  submitNextTimeText: {
    color: themeVariables.fontColors.extraLight,
    cursor: 'pointer',
    fontSize: 14,
    ':hover': {
      color: themeVariables.fontColors.medium,
    },
  },

  chip: {
    display: 'flex',
    height: '100%',
    label: {
      fontSize: 13,
      borderRadius: '0.5rem',
      padding: '0px 10px',
      border: `1px solid ${themeVariables.colors.light300} !important`,
      color: dark?.dark
        ? themeVariables.fontColors.white
        : themeVariables.fontColors.dark,
      backgroundColor: 'none',
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      ':hover': {
        backgroundColor: dark?.dark
          ? 'transparent'
          : `${themeVariables.colors.white} !important`,
        border: `1px solid ${theme.colors.blue[3]} !important`,
      },
      ':active': {},
      '&[data-checked]': {
        border: `1px solid ${theme.colors.blue[3]} !important`,
        color: theme.colors.blue[3],
      },
      span: {
        display: 'none',
      },
    },
  },
}));

export default function UserRegisterModal() {
  const { dark } = isDark();
  const { classes } = createUserRegisterModalStyle({ dark });
  const { urlSearchParams } = useReplaceQueryParams();

  const [opened, { close }] = useDisclosure(!!urlSearchParams.get('welcome'));

  const [data, setData] = useState({
    jobList: [],
    techstackCategoryList: [],
  });

  const [form, setform] = useState<any>({
    job: '',
    interested_category: [],
    newsletter_granted: 'true',
  });

  const handleSubmitButton = () => {
    const { job, interested_category, newsletter_granted } = form;
    API_ENDPOINT.auth.updateRegisterUser({
      job,
      interested_category,
      newsletter_granted: newsletter_granted == 'true',
    });
    close();
  };

  const user = useAtomValue(userInfo);

  useEffect(() => {
    getBasicInfo();
    if (urlSearchParams.get('welcome') && user.username) {
      mixpanelIdentifyEmail(user);
    }
  }, [urlSearchParams.get('welcome'), user.username]);

  const getBasicInfo = async () => {
    const [jobList, techstackCategoryList] = await Promise.all([
      API_ENDPOINT.auth.getBasicInfo(),
      API_ENDPOINT.techstack.getCategoryList(),
    ]);

    setData({
      jobList: jobList.data.job_list,
      techstackCategoryList: techstackCategoryList.data,
    });
  };

  return (
    <Modal
      opened={opened}
      size={'md'}
      centered
      closeOnClickOutside={false}
      onClose={close}
      withCloseButton={false}
      padding={24}
      sx={{ zIndex: 2000 }}
    >
      <DMText className={classes.title} dColor={themeVariables.darkMode.normal}>
        추가 정보
      </DMText>
      <Text className={classes.description}>
        정보를 바탕으로 이용자님께 맞는 컨텐츠를 추천드리고 있어요
      </Text>
      <Stack spacing={16} data-autofocus>
        <Select
          label={'직무'}
          variant={'filled'}
          withinPortal
          placeholder={'직무를 선택해주세요'}
          classNames={{ label: classes.formLabel }}
          data={data.jobList.map((job) => ({ value: job, label: job }))}
          onChange={(value) => setform({ ...form, job: value })}
        />

        <Box w={'100%'}>
          <Text className={classes.formLabel}>관심 분야</Text>
          <Group spacing={3}>
            <Chip.Group
              multiple
              onChange={(value) =>
                setform({ ...form, interested_category: value })
              }
            >
              {data.techstackCategoryList.map((category) => (
                <Chip key={category} className={classes.chip} value={category}>
                  {category}
                </Chip>
              ))}
            </Chip.Group>
          </Group>
        </Box>

        <Radio.Group
          mt={18}
          classNames={{ label: classes.formLabel }}
          label={
            <Group spacing={6}>
              뉴스레터 수신 설정
              <QuestionMarkTooltip
                size={14}
                label={'매주 인기있는 컨텐츠를 보내드려요'}
              />
            </Group>
          }
          onChange={(value) => setform({ ...form, newsletter_granted: value })}
        >
          <Group>
            <Radio value={'true'} label="받아볼게요" />
            <Radio value={'false'} label="받기 싫어요" />
          </Group>
        </Radio.Group>

        <Group position={'apart'} pt={12}>
          <Text className={classes.submitNextTimeText} onClick={close}>
            다음에 작성하기
          </Text>
          <Button type={ButtonTypes.solidColored} onClick={handleSubmitButton}>
            확인
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
